import { z } from 'zod';
import { AdminScope, AppRole, Role, UserStatus } from '../enums.js';
import { uuid } from './common.js';

// Public user shape returned by /auth/me and other endpoints.
export const publicUser = z.object({
  id: uuid,
  email: z.string().email(),
  full_name: z.string(),
  phone: z.string().nullable(),
  // Plain string (not .url()): when R2 is not provisioned an uploaded avatar is
  // stored as a base64 data URL, which is not guaranteed to satisfy .url().
  avatar_url: z.string().nullable(),
  role: z.enum([Role.RESIDENT, Role.SECURITY, Role.STAFF]),
  app_role: z.enum([AppRole.ADMIN, AppRole.INTERNAL, AppRole.RESIDENT]).optional(),
  status: z.enum([UserStatus.PENDING, UserStatus.ACTIVE, UserStatus.SUSPENDED]),
  cluster_id: uuid,
  unit: z
    .object({
      id: uuid,
      label: z.string(),
      block: z.string(),
      number: z.string(),
    })
    .nullable(),
  permissions: z.array(z.string()).optional(),
  // Admin tier (staff only) — governs which clusters they can switch between and
  // gates upper-tier-only pages like Staff & Security. Absent for residents.
  admin_scope: z.enum([AdminScope.CLUSTER, AdminScope.DEVELOPER, AdminScope.PLATFORM]).optional(),
});
export type PublicUser = z.infer<typeof publicUser>;

// Token pair returned by signup / login / refresh.
export const tokenPair = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  access_token_expires_at: z.string().datetime(),
});
export type TokenPair = z.infer<typeof tokenPair>;

export const authResponse = z.object({
  user: publicUser,
  tokens: tokenPair,
});
export type AuthResponse = z.infer<typeof authResponse>;

// Email signup request
export const signupEmailRequest = z.object({
  email: z.string().email().toLowerCase().trim(),
  password: z.string().min(8).max(72),
  full_name: z.string().min(1).max(120).trim(),
});
export type SignupEmailRequest = z.infer<typeof signupEmailRequest>;

export const signupEmailResponse = z.object({
  otp_id: uuid,
  expires_at: z.string().datetime(),
});

// Email OTP verify
export const verifyEmailOtpRequest = z.object({
  otp_id: uuid,
  code: z.string().length(6).regex(/^\d{6}$/),
});

// Google sign-in / signup (same endpoint, idempotent)
export const googleAuthRequest = z.object({
  id_token: z.string(),
  // Mobile sends device label for refresh-token tracking
  device_label: z.string().max(100).optional(),
});

// Email login
export const loginEmailRequest = z.object({
  email: z.string().email().toLowerCase().trim(),
  password: z.string().min(1),
  device_label: z.string().max(100).optional(),
});

// Refresh token
export const refreshTokenRequest = z.object({
  refresh_token: z.string(),
});

// Link unit (consumes invite code, flips status PENDING -> ACTIVE)
export const linkUnitRequest = z.object({
  code: z
    .string()
    .min(4)
    .max(40)
    .transform((s) => s.toUpperCase().trim()),
});

// Change password (authenticated user changes their own)
export const changePasswordRequest = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(8).max(72),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;

// Update own profile (authenticated staff user). Both fields optional so the
// caller can change just the name, just the avatar, or both — but at least one
// must be present. avatar_url is nullable so it can be cleared (remove photo).
// The max mirrors the other image fields' server caps (~2.9M chars ≈ 2 MB),
// since an avatar with R2 absent is stored as a base64 data URL in D1.
// Named editProfileRequest (not updateProfileRequest) to avoid colliding with
// the resident/mobile schema of that name in ./directory.ts (PATCH /api/v1/me).
export const editProfileRequest = z
  .object({
    full_name: z.string().trim().min(1).max(120).optional(),
    avatar_url: z.string().max(2_900_000).nullable().optional(),
  })
  .refine((v) => v.full_name !== undefined || v.avatar_url !== undefined, {
    message: 'Provide full_name and/or avatar_url',
  });
export type EditProfileRequest = z.infer<typeof editProfileRequest>;

// Password reset — request a code
export const requestPasswordResetRequest = z.object({
  email: z.string().email().toLowerCase().trim(),
});
export type RequestPasswordResetRequest = z.infer<typeof requestPasswordResetRequest>;

export const requestPasswordResetResponse = z.object({
  otp_id: uuid,
  expires_at: z.string().datetime(),
});
export type RequestPasswordResetResponse = z.infer<typeof requestPasswordResetResponse>;

// Password reset — confirm with code + new password
export const confirmPasswordResetRequest = z.object({
  otp_id: uuid,
  code: z.string().length(6).regex(/^\d{6}$/),
  new_password: z.string().min(8).max(72),
});
export type ConfirmPasswordResetRequest = z.infer<typeof confirmPasswordResetRequest>;

// FCM device registration
export const registerDeviceRequest = z.object({
  fcm_token: z.string().min(10),
  platform: z.enum(['ANDROID', 'IOS', 'WEB']),
  device_label: z.string().max(100).optional(),
});
